import { createFileRoute } from "@tanstack/react-router"
import { UseCasePage } from "@/components/use-case-page"
import { publicPageHead } from "@/lib/seo"

const title = "YouTube Shorts Generator with AI | intoshort"
const description =
  "Convert YouTube videos to shorts with AI. Paste a public video link, select moments, edit captions and framing, then export the shorts you approve."

export const Route = createFileRoute("/tools/youtube-to-shorts")({
  head: () =>
    publicPageHead({ path: "/tools/youtube-to-shorts", title, description }),
  component: YouTubeToShortsPage,
})

function YouTubeToShortsPage() {
  return (
    <UseCasePage
      badge="YouTube to Shorts Converter"
      eyebrow="Reuse the video you already published"
      title="YouTube Shorts generator for your long videos."
      lead="Use intoshort as a YouTube to Shorts converter for videos you are allowed to reuse. Paste a public YouTube link and let AI select passages for captioned vertical shorts. Review the moments, adjust the cuts and export your chosen clips."
      sourceLine="Works with public YouTube video links you are allowed to reuse."
      proofTitle="An AI first cut from your YouTube video."
      proofCopy="intoshort handles the repetitive first pass while you keep the editorial decisions. It reads the source, selects passages that match your instructions, calculates the cuts and prepares drafts for review."
      proofPoints={[
        "Choose the kinds of moments, target duration, caption style and output quality before processing.",
        "Review the suggested passage and adjust the cut instead of accepting a black-box result.",
        "Correct captions and export only the shorts you approve.",
      ]}
      stepsTitle="How to convert a YouTube video to shorts."
      stepsCopy="The source stays connected to one clear workflow, so you can follow the processing state and return to the same project when the drafts are ready."
      steps={[
        {
          title: "Paste the public YouTube link",
          copy: "intoshort checks that the URL is supported and reads the available source information before you configure the result.",
        },
        {
          title: "Describe the moments you want",
          copy: "Set the number and length of moments, captions, resolution and the editorial direction for the selection.",
        },
        {
          title: "Let AI prepare the first cut",
          copy: "The source is transcribed, aligned and analyzed. The model references real transcript segments; intoshort validates the timing and makes the cuts.",
        },
        {
          title: "Review, refine and export",
          copy: "Play every suggestion, change the cut or caption treatment, and download only the versions you want to use.",
        },
      ]}
      limitsTitle="The link still has to be usable."
      limits={[
        "The video must be public and accessible. Private, age-restricted, region-blocked or protected sources can fail to import.",
        "You need the right to download, edit and republish the source. A public URL does not grant reuse rights.",
        "AI can miss context, visual-only moments or a better edit. No clipper can guarantee reach or virality, so review remains necessary.",
      ]}
      questions={[
        {
          question: "Can I paste any YouTube URL?",
          answer:
            "Use a direct, public YouTube video URL. Shorts URLs are accepted when the underlying video is accessible, but playlists, private videos and some restricted videos are not valid sources.",
        },
        {
          question: "Does intoshort post the clips automatically?",
          answer:
            "No. The current workflow prepares editable drafts for review and export. Nothing is published just because a video finished processing.",
        },
        {
          question: "Can I change the AI-selected moment?",
          answer:
            "Yes. The suggestions are a starting point. You can review the passage, adjust the cut and change the caption style, size and position before export.",
        },
      ]}
      related={[
        {
          eyebrow: "Long recordings",
          title: "Twitch VOD to Shorts",
          copy: "Work through public streams without treating every minute as a clip.",
          href: "/tools/twitch-vod-to-shorts",
        },
        {
          eyebrow: "Upload or link",
          title: "Long Video to Shorts",
          copy: "Use the same review workflow for a video file or supported link.",
          href: "/tools/long-video-to-short-video",
        },
      ]}
    />
  )
}
